import sb from '@/utils/sb'
import { supabaseAuthActions } from '@/utils/store/supabaseAuth'
import { type EmailOtpType } from '@supabase/supabase-js'
import { type LoaderFunctionArgs, redirect } from 'react-router'
import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router'

// 服务端 loader：处理来自 query 参数的认证（备用方案）
export async function loader({ request }: LoaderFunctionArgs) {
  const requestUrl = new URL(request.url)
  // 同时支持 token_hash 和 token 参数（兼容不同版本的 Supabase）
  const token_hash = requestUrl.searchParams.get('token_hash') || requestUrl.searchParams.get('token')
  const type = requestUrl.searchParams.get('type') as EmailOtpType | null
  const _next = requestUrl.searchParams.get('next')
  
  // 默认跳转到首页
  const next = _next || '/'

  console.log('URL query params:', {
    url: request.url,
    token_hash,
    type,
    next,
    allParams: Object.fromEntries(requestUrl.searchParams.entries())
  })

  const supabase = sb;

  // 如果有 query 参数中的 token，尝试验证（备用方案）
  if (token_hash && type) {
    const { error } = await supabase.auth.verifyOtp({
      type,
      token_hash,
    })
    if (!error) {
      // 更新认证状态
      await supabaseAuthActions.update();
      return redirect(next, {  })
    } else {
      console.error('OTP verification failed:', error)
      return redirect(`/s_auth/error?error=${encodeURIComponent(error?.message || 'Verification failed')}`)
    }
  }

  // 如果没有 query 参数，返回 null 让客户端组件处理 URL hash
  return null
}

// 客户端组件：处理 URL hash 中的 token（主要方案）
export default function AuthConfirm() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const _next = searchParams.get('next')
  
  // 默认跳转到首页
  const next = _next || '/'

  console.log('AuthConfirm - next path:', { original: _next, processed: next })

  useEffect(() => {
    const handleHashParams = async () => {
      // Supabase 在重定向时会将 access_token 和 refresh_token 放在 URL hash 中
      const hashParams = new URLSearchParams(window.location.hash.substring(1))
      const access_token = hashParams.get('access_token')
      const refresh_token = hashParams.get('refresh_token')
      const type = hashParams.get('type')
      const error = hashParams.get('error')
      const error_description = hashParams.get('error_description')

      console.log('URL hash params:', {
        hash: window.location.hash,
        access_token: access_token?.substring(0, 20) + '...',
        refresh_token: refresh_token?.substring(0, 20) + '...',
        type,
        error,
        error_description
      })

      if (error) {
        navigate(`/s_auth/error?error=${encodeURIComponent(error_description || error)}`)
        return
      }

      if (access_token && refresh_token) {
        // Supabase SDK 会自动处理 hash 中的 token，建立 session
        // 等待一小段时间确保 session 已建立
        await new Promise(resolve => setTimeout(resolve, 100))
        
        // 更新认证状态
        await supabaseAuthActions.update()
        
        // 清除 URL hash 并跳转
        window.history.replaceState(null, '', window.location.pathname + window.location.search)
        navigate(next, { replace: true })
      } else if (!searchParams.get('token_hash') && !searchParams.get('token')) {
        // 既没有 hash 参数，也没有 query 参数
        navigate('/s_auth/error?error=' + encodeURIComponent('No authentication token found'))
      }
      // 如果有 query 参数，会由 loader 处理
    }

    handleHashParams()
  }, [navigate, next, searchParams])

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]" />
        <p className="text-gray-600">正在验证您的身份...</p>
      </div>
    </div>
  )
}
